import { Tractor, FlaskConical, PackageCheck, Ship, FileCheck, Globe } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const PROCESS_STEPS = [
  {
    Icon: Tractor,
    title: "Farm Sourcing",
    desc: "Direct procurement from certified partner farms. Field inspections and harvest monitoring ensure optimal maturity and quality.",
    duration: "Day 0-7",
  },
  {
    Icon: PackageCheck,
    title: "Receiving & Cleaning",
    desc: "Grain arrives at our facility for initial cleaning, screening and moisture testing. Rejected batches are returned immediately.",
    duration: "Day 8-10",
  },
  {
    Icon: FlaskConical,
    title: "Lab Testing",
    desc: "Comprehensive analysis for purity, moisture, foreign matter, broken grains, and contaminants. Third-party COA arranged if required.",
    duration: "Day 11-15",
  },
  {
    Icon: PackageCheck,
    title: "Processing & Grading",
    desc: "Advanced color sorting, grading by size, and packaging according to customer specifications. Metal detection on all lines.",
    duration: "Day 16-25",
  },
  {
    Icon: FileCheck,
    title: "Documentation",
    desc: "Phytosanitary certificates, COA, packing lists, and commercial invoices prepared. Customs clearance arranged.",
    duration: "Day 26-35",
  },
  {
    Icon: Ship,
    title: "Export Logistics",
    desc: "Container loading at Karachi port. Full tracking provided from gate-in to vessel departure. CIF or FOB terms available.",
    duration: "Day 36-45",
  },
];

const SPECS = [
  { param: "Purity", basmati: "95-98%", white: "96-98%", parboiled: "96-98%" },
  { param: "Moisture", basmati: "12-14%", white: "13-15%", parboiled: "13-14%" },
  { param: "Foreign Matter", basmati: "< 2%", white: "< 1.5%", parboiled: "< 1%" },
  { param: "Broken Grains", basmati: "< 5%", white: "< 5%", parboiled: "< 4%" },
  { param: "Discolored/Damaged", basmati: "< 3%", white: "< 2%", parboiled: "< 2%" },
];

export default function Process() {
  return (
    <div data-testid="page-process" className="py-16 md:py-20">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="overline">End-to-End Quality</div>
        <h1 className="mt-4 font-serif text-5xl md:text-7xl tracking-tight max-w-4xl">
          From field to vessel: A controlled pipeline.
        </h1>
        <p className="mt-8 text-foreground/75 text-lg max-w-3xl leading-relaxed">
          Every shipment follows our standardized 45-day export process. Fully traceable, 
          lab-verified, and documented at every stage.
        </p>

        {/* Process Steps */}
        <div className="mt-20">
          <div className="overline mb-10">Six-Stage Pipeline</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
            {PROCESS_STEPS.map((step, i) => (
              <div key={i} className="bg-[#0A0909] p-8">
                <div className="flex items-start justify-between">
                  <step.Icon size={28} className="text-[#3FD97A]" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {step.duration}
                  </span>
                </div>
                <h3 className="mt-6 font-serif text-2xl">{step.title}</h3>
                <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{step.desc}</p>
                <div className="mt-4 font-mono text-[11px] text-[#3FD97A]">STEP {String(i + 1).padStart(2, '0')}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline Visual */}
        <div className="mt-20 border-t border-b border-border py-12">
          <div className="flex items-center justify-between">
            <div className="text-center">
              <Globe size={32} className="text-[#3FD97A] mx-auto" />
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Farm Gate</p>
              <p className="mt-1 font-serif text-xl">Day 0</p>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-border via-[#3FD97A] to-border mx-8"></div>
            <div className="text-center">
              <Ship size={32} className="text-[#3FD97A] mx-auto" />
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Port Departure</p>
              <p className="mt-1 font-serif text-xl">Day 45</p>
            </div>
          </div>
        </div>

        {/* Technical Specifications */}
        <div className="mt-28">
          <div className="overline mb-10">Standard Technical Parameters</div>
          <div className="border border-border">
            <div className="p-6 bg-[#0A0909] border-b border-border">
              <h2 className="font-serif text-3xl">Typical Grade A Specifications</h2>
              <p className="mt-2 text-sm text-foreground/70">Reference values. Actual specs vary by product and customer requirements.</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">Parameter</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">Basmati Rice</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">White Rice</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">Parboiled Rice</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {SPECS.map((spec, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-sm font-semibold">{spec.param}</TableCell>
                    <TableCell>{spec.basmati}</TableCell>
                    <TableCell>{spec.white}</TableCell>
                    <TableCell>{spec.parboiled}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Quality Assurance */}
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="border border-border p-10 bg-[#0A0909]">
            <FlaskConical size={32} className="text-[#3FD97A]" />
            <h3 className="mt-6 font-serif text-3xl">In-house Laboratory</h3>
            <p className="mt-4 text-foreground/75 leading-relaxed">
              Our on-site lab performs rapid tests for moisture, purity, and visual quality. 
              Equipped with moisture meters, microscopes, and calibrated scales.
            </p>
          </div>
          <div className="border border-border p-10 bg-[#0A0909]">
            <FileCheck size={32} className="text-[#3FD97A]" />
            <h3 className="mt-6 font-serif text-3xl">Third-Party Certification</h3>
            <p className="mt-4 text-foreground/75 leading-relaxed">
              For export compliance, we arrange SGS, Bureau Veritas, or Intertek inspections. 
              Certificates of Analysis (COA) provided with every shipment on request.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
